// Firebase Cloud Messaging client — token registration + foreground handlers.
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { supabase } from "./supabase";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Loud, early check — silent FCM failure is a class of bug we want to avoid.
// If env vars are missing (typically CRA dev-server not restarted after .env edit),
// log a single visible error so the developer notices immediately.
const _missing = ["apiKey", "projectId", "messagingSenderId", "appId"].filter(
  (k) => !firebaseConfig[k]
);
if (_missing.length > 0) {
  // eslint-disable-next-line no-console
  console.error(
    `[firebase] missing config values: ${_missing.join(", ")}. ` +
      `Restart the frontend after editing /app/frontend/.env (CRA bakes env vars at startup).`
  );
}

let app;
let messagingInstance = null;

export function initFirebase() {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  return app;
}

async function getMessagingSafe() {
  if (messagingInstance) return messagingInstance;
  try {
    const ok = await isSupported();
    if (!ok) return null;
    initFirebase();
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch (e) {
    console.warn("[fcm] not supported", e?.message);
    return null;
  }
}

/**
 * Request notification permission and register an FCM token for the signed-in user.
 * Returns the token (or null if user denies / browser unsupported).
 */
export async function registerForPush(userId) {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return null;

  const messaging = await getMessagingSafe();
  if (!messaging) return null;

  // Ask the browser permission first (must be triggered from a user gesture).
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return null;

  // Make sure the service worker is registered first.
  let swReg;
  try {
    swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    await navigator.serviceWorker.ready;
  } catch (e) {
    console.error("[fcm] sw register failed", e);
    return null;
  }

  // Get the FCM token using our VAPID public key.
  let token;
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
  } catch (e) {
    console.error("[fcm] getToken failed", e?.message);
    return null;
  }

  if (token && userId) {
    // Upsert into fcm_tokens
    await supabase
      .from("fcm_tokens")
      .upsert(
        { user_id: userId, token, platform: "web", updated_at: new Date().toISOString() },
        { onConflict: "user_id,token" }
      );
  }
  return token;
}

/**
 * Foreground message handler — fires when a push arrives while the app is open.
 * Use it to show a Sonner toast instead of an OS-level notification.
 */
export async function onForegroundMessage(handler) {
  const messaging = await getMessagingSafe();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
